import {ProductScript} from "../../../../api/Products";
import {
    Alert,
    Button,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    TextField
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import React, {Suspense, useRef, useState} from "react";
import {useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import {grey} from "@mui/material/colors";

const EditScriptField = React.lazy(() => import("./EditScriptField").then(f => ({default: f.EditScriptField})));

export function EditScript(props: {
    value: ProductScript;
    onClose(): void;
    onSave(value: ProductScript): void
}) {
    return (
        <Suspense fallback={<LoadingEl />}>
            <EditScriptInner {...props} />
        </Suspense>
    )
}

function EditScriptInner(props: {
    value: ProductScript;
    onClose(): void;
    onSave(value: ProductScript): void
}) {
    const [name, setName] = useState(props.value.name);

    // don't re-render for code to avoid re-creating EditScriptField every time
    const codeRef = useRef(props.value.script || blankScript)

    const check = useAsyncAction(async (input: {
        code: string;
        onDone?(): void
    }) => {

        const rs = await api.products.checkScript({
            script: input.code,
        });

        if(rs.status === "ok") {
            if(input.onDone) {
                input.onDone();
            }
        }

        return rs;
    }, []);

    const checkR = check.result;
    console.log("checkR?.status", checkR?.status)

    const submit = () => {
        if(name.length < 4) return;
        const code = codeRef.current;

        check.callback(({
            code: code,
            onDone: () => {
                props.onSave(Object.assign({}, props.value, {
                    name: name,
                    script: codeRef.current,
                }))
                props.onClose();
            }
        }))
    };

    return (
        <Dialog2 open onClose={() => props.onClose()} maxWidth="xl" fullWidth onEnterPress={submit}>
            <DialogTitle>
                Edit Script
            </DialogTitle>
            <DialogContent>
                <div style={{height: 8}} />
                <TextField label="Name"
                           value={name}
                           onChange={e => {
                               let value: string = e.currentTarget.value;
                               value = value.replace(/[^A-z_]+/g, "");
                               setName(value);
                           }}
                           helperText="e.g. sku_handing (no spaces or special characters)" />

                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                    width: "100%",
                }}>
                    <div style={{
                        display: "flex",
                        flex: 1,
                        flexDirection: "column",
                    }}>
                        <EditScriptField key="main" value={codeRef.current} onChange={v => {
                            codeRef.current = v;
                        }} />

                        {checkR?.status === "error" && <Alert style={{marginTop: 8}} severity="error">
                            {checkR.line > 0 && <div>{checkR.line} {checkR.context}</div>}
                            <div>{checkR.detail}</div>
                        </Alert>}
                    </div>
                    <div style={{
                        width: 400,
                        display: "flex",
                        opacity: 0.7,
                    }}>
                        <EditScriptField key="demo" value={demoCode} />
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => {
                    check.callback({
                        code: codeRef.current,
                    });
                }}>Check Code</Button>

                {checkR?.status === "error" ?
                        <Chip color="error" label="See error above" /> :
                    checkR?.status === "ok" ? <>
                        <Chip color="success" label="Code is ok" />
                    </> :
                    null}
                {check.LoadingElement}
                <div style={{flex: 1}}></div>
                <Button onClick={() => props.onClose()}>Cancel</Button>
                <Button disabled={name.length < 4} onClick={submit}>Update</Button>
            </DialogActions>
        </Dialog2>
    )
}

// fields:
// "nominal.width"
// "nominal.height"
// "inactive.width"
// "opening.type"
// "opening.subType"
// "opening.handing"
// "opening.qty"
// "qty"
// "opening.flipDoubleActiveHanding"
// "handing"
// "door.handing"
// "door.width"
// "door.height"
// "door.thickness"
// "frame.profile"
// "frame.jambDepth"
// "frame.width"
// "frame.height"
// "frame.anchorQtyOverride"

const blankScript = `if handing = LH
    return L2

error: invalid handing`

const demoCode = `# Example code

# variables:
# door.width, door.height, door.thickness, door.handing
# frame.width, frame.height, frame.jambDepth, frame.profile
# nominal.width, nominal.height
# qty
# label
# opening.type, opening.subType, opening.handing, opening.qty, opening.flipDoubleActiveHanding

# compare things
if nominal.width = 4'2 11/16 or nominal.width < 2'0 or nominal.width >= 5'0
  return EXT # output EXT
  
if label = 20 min
  return nominal.width * 2 # do math *, /, +, -

# use metric or imperial inter-changably
if nominal.height < 1000mm
  # use max/min functions to limit values to a certain range
  return min(max(nominal.height, 1000mm), 2000mm)
  
# trigger errors
if isblank(handing) 
  error: invalid width, should be over 2'0

if handing = RHR
  return LH

if handing = LH
  return LH

# do complicated math
return 5 * 2'0 + 7 / nominal.width - 200mm`

function LoadingEl() {
    return (
        <div style={{color: grey["200"]}}>
            <LinearProgress />
        </div>
    )
}